import { pointAtStation, type Alignment } from '@/core/alignment';
import * as THREE from 'three';

/**
 * The station list every swath consumer samples at: uniform samples no more
 * than `sampleStep` apart across `[fromStation, fromStation + span]`, PLUS
 * every original polyline vertex strictly inside that range.
 *
 * The vertex insertion is the point. Without it a uniform sampler walks
 * straight past a corner — an alignment that turns 90 deg at station 100 gets
 * sampled at 85.7 and 114.3 and the swath edge cuts the corner off, so the
 * ribbon mitres visibly wrong exactly where a reader is looking hardest.
 * Inserting the vertex only ever SPLITS a uniform span, so the "no gap wider
 * than sampleStep" contract still holds and the sample count only grows by
 * the number of vertices in range.
 */
export function sampleStations(
  a: Alignment,
  fromStation: number,
  toStation: number,
  sampleStep: number,
): Float64Array {
  const span = Math.max(0, toStation - fromStation);
  const uniform = Math.max(2, Math.ceil(span / Math.max(sampleStep, 1e-3)) + 1);
  // Tolerance is absolute metres: stations are O(1e4) at most and two samples
  // closer than a micrometre are the same sample for every downstream use.
  const EPS = 1e-6;
  const to = fromStation + span;
  const out: number[] = [];
  const push = (s: number) => {
    if (out.length > 0 && s - out[out.length - 1] <= EPS) return;
    out.push(s);
  };
  const cum = a.cumLengths;
  let v = 0;
  // Both lists are ascending, so one merge pass produces the sorted union.
  while (v < cum.length && cum[v] <= fromStation + EPS) v++;
  for (let i = 0; i < uniform; i++) {
    const s = i === uniform - 1 ? to : fromStation + (span * i) / (uniform - 1);
    while (v < cum.length && cum[v] < s - EPS && cum[v] < to - EPS) push(cum[v++]);
    // A vertex the uniform sample already lands on must not be emitted twice.
    if (v < cum.length && Math.abs(cum[v] - s) <= EPS) v++;
    push(s);
  }
  // A zero-length (or reversed) range still has to produce a strip rather
  // than throw — two coincident samples give a degenerate, invisible ribbon.
  if (out.length < 2) out.push(out[0]);
  return Float64Array.from(out);
}

/**
 * Sample the two swath edges (world-space, no origin subtraction) along the
 * alignment centreline. Shared by `buildSwathGeometry` (filled ribbon),
 * `buildSwathOutline` (closed loop) and Task 14's per-bin corridor bands
 * (`bandGeometry.ts`) so the sampling math — station spacing, vertex
 * insertion, left-normal sign convention — lives in exactly one place.
 */
export function sampleSwathEdges(
  a: Alignment,
  swathLeft: number,
  swathRight: number,
  fromStation: number,
  toStation: number,
  sampleStep: number,
): { n: number; stations: Float64Array; left: Float64Array; right: Float64Array } {
  const stations = sampleStations(a, fromStation, toStation, sampleStep);
  const n = stations.length;
  const left = new Float64Array(n * 3);
  const right = new Float64Array(n * 3);
  for (let i = 0; i < n; i++) {
    const s = stations[i];
    const p = pointAtStation(a, s);
    // 2D left normal of the heading (tx, ty) is (-ty, tx); left is NEGATIVE
    // offset, so the left edge is at +swathLeft along +normal (see
    // core/alignment.ts's `projectPoint` sign convention).
    const nx = -p.ty;
    const ny = p.tx;
    const o = i * 3;
    left[o] = p.x + nx * swathLeft;
    left[o + 1] = p.y + ny * swathLeft;
    left[o + 2] = p.z;
    right[o] = p.x - nx * swathRight;
    right[o + 1] = p.y - ny * swathRight;
    right[o + 2] = p.z;
  }
  return { n, stations, left, right };
}

/**
 * Build a quad-strip ribbon between the two swath edges, sampled along the
 * alignment centreline. Vertices are relative to `origin` — UTM coordinates
 * jitter visibly at Float32 precision, so every 3D consumer of an alignment
 * hangs off a floating-origin group (same rule as `AlignmentOverlay.tsx:295`,
 * CLAUDE.md's screen-space/float-origin section).
 *
 * This is a pure, DOM-free module (T9.1 / rulings.md) so it is unit-testable
 * without r3f: `SwathPreview.tsx` (dialog-time preview, Task 9) and Task 14's
 * `StationBands.tsx` (persisted 3D corridor bands) both import this single
 * copy of the sampling loop — do not re-implement it a second time.
 */
export function buildSwathGeometry(
  a: Alignment,
  swathLeft: number,
  swathRight: number,
  fromStation: number,
  toStation: number,
  sampleStep: number,
  origin: THREE.Vector3,
): THREE.BufferGeometry {
  const { n, left, right } = sampleSwathEdges(a, swathLeft, swathRight, fromStation, toStation, sampleStep);
  const positions = new Float32Array(n * 2 * 3);
  for (let i = 0; i < n; i++) {
    const li = i * 3;
    const o = i * 6;
    positions[o] = left[li] - origin.x;
    positions[o + 1] = left[li + 1] - origin.y;
    positions[o + 2] = left[li + 2] - origin.z;
    positions[o + 3] = right[li] - origin.x;
    positions[o + 4] = right[li + 1] - origin.y;
    positions[o + 5] = right[li + 2] - origin.z;
  }
  const index: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const a0 = i * 2;
    const b0 = a0 + 1;
    const a1 = a0 + 2;
    const b1 = a0 + 3;
    index.push(a0, b0, a1, b0, b1, a1);
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setIndex(index);
  geom.computeVertexNormals();
  return geom;
}

/**
 * Build the swath's closed outline — `left[0..n-1]` then `right[n-1..0]`,
 * relative to `origin` — for drawing as a `THREE.LineLoop` (2 px look: one
 * clean silhouette, not `wireframeGeometry`'s per-triangle cross-hatch, which
 * draws every diagonal edge of a 1 000-quad corridor and reads as noise on
 * screen — fix round 1, item 6).
 */
export function buildSwathOutline(
  a: Alignment,
  swathLeft: number,
  swathRight: number,
  fromStation: number,
  toStation: number,
  sampleStep: number,
  origin: THREE.Vector3,
): THREE.BufferGeometry {
  const { n, left, right } = sampleSwathEdges(a, swathLeft, swathRight, fromStation, toStation, sampleStep);
  const positions = new Float32Array(n * 2 * 3);
  for (let i = 0; i < n; i++) {
    const li = i * 3;
    const o = i * 3;
    positions[o] = left[li] - origin.x;
    positions[o + 1] = left[li + 1] - origin.y;
    positions[o + 2] = left[li + 2] - origin.z;
  }
  for (let i = 0; i < n; i++) {
    const j = n - 1 - i;
    const ri = j * 3;
    const o = (n + i) * 3;
    positions[o] = right[ri] - origin.x;
    positions[o + 1] = right[ri + 1] - origin.y;
    positions[o + 2] = right[ri + 2] - origin.z;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return geom;
}
